/**
 * Platform / menu visibility for sidebar, main shell, and home dashboard.
 * RBAC: require module.read or any granted field *.read under that module (e.g. rom.index.read).
 *
 * Skip policy: ananunaki superuser bypasses all checks below (full access within env-enabled modules).
 */

import { isUserAnanunaki } from "./ananunaki";
import { getModuleNamesWithReadAccess, hasPermission, userHasModuleMenuAccess } from "./permissionResolver";

export { isUserAnanunaki };

export function userHasReadPermission(user, permissionModel) {
	if (!permissionModel) return false;
	return userHasModuleMenuAccess(user, permissionModel);
}

function menuAccessPermissionModel(item) {
	return item?.accessPermissionModel || item?.permissionModel;
}

function getUserReadPermissionNames(user) {
	if (isUserAnanunaki(user)) return null;
	const names = getModuleNamesWithReadAccess(user);
	return names && names.length ? names : [];
}

const OFFICE_MANAGEMENT_KEYS = [
	"office.create", "office.update", "office.delete",
	"office.locations.create", "office.locations.update", "office.locations.delete",
	"office.floors.create", "office.floors.update", "office.floors.delete",
	"office.rooms.create", "office.rooms.update", "office.rooms.delete",
	"office.desks.create", "office.desks.update", "office.desks.delete",
	"office.inventory.create", "office.inventory.update", "office.inventoryMaintenance.update",
	"office.bookingPolicies.create", "office.bookingPolicies.update", "office.resources.update",
	"office.serviceRequests.update",
	"role.read"
];

export function isOfficeManagementUser(user) {
	if (isUserAnanunaki(user)) return true;
	return OFFICE_MANAGEMENT_KEYS.some((key) => hasPermission(user, key));
}

function isAudienceAllowed(item, user) {
	if (item?.officeAudience === "management" && !isOfficeManagementUser(user)) return false;
	if (item?.officeAudience === "employee" && isOfficeManagementUser(user)) return false;
	if (Array.isArray(item?.requiresAnyPermission) && item.requiresAnyPermission.length) {
		return item.requiresAnyPermission.some((key) => hasPermission(user, key));
	}
	return true;
}

/** Same rules as sidebar submenu filtering. */
export function filterMenuItemsByReadPermission(menuItems, user) {
	if (!menuItems || !Array.isArray(menuItems)) return [];
	const superuser = isUserAnanunaki(user);

	return menuItems
		.map((item) => {
			if (!isAudienceAllowed(item, user)) return null;
			if (item.subMenu) {
				const filteredSubMenu = item.subMenu.filter((subItem) =>
					isAudienceAllowed(subItem, user) &&
					(superuser || userHasReadPermission(user, menuAccessPermissionModel(subItem)))
				);
				if (filteredSubMenu.length > 0) {
					return { ...item, subMenu: filteredSubMenu };
				}
				return null;
			}
			return superuser || userHasReadPermission(user, menuAccessPermissionModel(item)) ? item : null;
		})
		.filter(Boolean);
}

/** Whether a top-level platform (accordion) should appear at all. */
export function isPlatformAllowedForUser(platform, user) {
	if (!platform) return false;
	if (isUserAnanunaki(user)) return true;

	const names = getUserReadPermissionNames(user);
	if (!names || names.length === 0) return false;

	if (
		platform.permissionModel &&
		userHasReadPermission(user, menuAccessPermissionModel(platform))
	) {
		return true;
	}

	return Boolean(
		platform.menu?.some((menuItem) => {
			if (
				menuItem.permissionModel &&
				userHasReadPermission(user, menuAccessPermissionModel(menuItem))
			) {
				return true;
			}
			if (menuItem.subMenu) {
				return menuItem.subMenu.some(
					(subItem) =>
						subItem.permissionModel &&
						userHasReadPermission(user, menuAccessPermissionModel(subItem))
				);
			}
			return false;
		})
	);
}

export function filterPlatformsByUserAccess(menuPlatforms, user) {
	if (!menuPlatforms?.length) return [];
	if (isUserAnanunaki(user)) return menuPlatforms;
	return menuPlatforms.filter((p) => isPlatformAllowedForUser(p, user));
}
